// Mobile-first app shell (task 3.5): the persistent chrome the electrician sees
// once signed in — a top header, a scrollable content area, and a fixed bottom
// navigation bar sized for one-handed / gloves-on use (Req 9.1).
//
// Navigation:
//   • Bottom nav gives one-tap access to each section (Home, Jobs, Quotes,
//     Invoices, Clients).
//   • The Home screen surfaces the three primary actions — New Job, New Quote,
//     New Invoice — each one tap away; from any section the "+ New" button is a
//     second tap. So the common create actions are always within one or two taps
//     (Req 9.3).
//
// The feature screens themselves (lists, capture forms, wired to the Phase 2 API)
// are built in task 3.6. This shell renders honest placeholders for them today but
// owns the routing, the layout, and the shared loading/error UI (via views.js,
// Req 9.4) that those screens will plug into.

import { h, clear, mount } from './lib/dom.js';
import { createRouter, routeToHash, parseRoute } from './router.js';
// Re-exported so the feature screens (task 3.6) get the shell's shared loading /
// error / empty states and async runner from one place (Req 9.4).
import { loadingView, errorView, emptyView, runAsync } from './views.js';
// The real feature screens (task 3.6): lists, detail/create/edit forms, photo
// capture and settings/rate card, all wired to the Phase 2 API.
import { renderFeatureScreen } from './screens/index.js';

// --- Icons (inline SVG; trusted static markup) -----------------------------

function icon(pathMarkup) {
  const span = document.createElement('span');
  span.className = 'icon';
  span.setAttribute('aria-hidden', 'true');
  span.innerHTML =
    `<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" ` +
    `stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${pathMarkup}</svg>`;
  return span;
}

const ICONS = {
  home: '<path d="M3 11l9-8 9 8"/><path d="M5 10v10h14V10"/>',
  jobs: '<path d="M9 3h6v3H9z"/><rect x="4" y="6" width="16" height="15" rx="2"/><path d="M8 12h8M8 16h5"/>',
  quotes: '<path d="M7 3h7l5 5v13H7z"/><path d="M14 3v5h5"/><path d="M10 13h6M10 17h6"/>',
  invoices: '<path d="M6 2h12v20l-3-2-3 2-3-2-3 2z"/><path d="M9 8h6M9 12h6"/>',
  clients: '<circle cx="9" cy="8" r="3"/><path d="M3 20a6 6 0 0 1 12 0"/><path d="M16 6a3 3 0 0 1 0 6M18 20a6 6 0 0 0-3-5"/>',
  settings: '<circle cx="12" cy="12" r="3"/><path d="M19 12a7 7 0 0 0-.1-1l2-1.5-2-3.4-2.3 1a7 7 0 0 0-1.7-1L14.5 3h-5l-.4 2.6a7 7 0 0 0-1.7 1l-2.3-1-2 3.4L3 11a7 7 0 0 0 0 2l-2 1.5 2 3.4 2.3-1a7 7 0 0 0 1.7 1L9.5 21h5l.4-2.6a7 7 0 0 0 1.7-1l2.3 1 2-3.4-2-1.5a7 7 0 0 0 .1-1z"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  signout: '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M16 17l5-5-5-5"/><path d="M21 12H9"/>',
  mic: '<rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 11a7 7 0 0 0 14 0"/><path d="M12 18v3M8 21h8"/>',
};

// --- Nav model -------------------------------------------------------------

// The bottom-nav tabs, left to right. Settings is intentionally NOT a bottom tab
// (it's a less-frequent action reached from the header gear); the four money/work
// sections plus Home fill the thumb row.
const NAV_ITEMS = [
  { section: 'home', label: 'Home' },
  { section: 'jobs', label: 'Jobs' },
  { section: 'quotes', label: 'Quotes' },
  { section: 'invoices', label: 'Invoices' },
  { section: 'clients', label: 'Clients' },
];

// Human-readable titles per section, for the header and screen headings.
const SECTION_TITLES = {
  home: 'Sparky',
  jobs: 'Jobs',
  quotes: 'Quotes',
  invoices: 'Invoices',
  clients: 'Clients',
  settings: 'Settings',
  voice: 'Voice',
};

// The three primary create actions on the Home screen (Req 9.3).
const QUICK_ACTIONS = [
  { section: 'jobs', label: 'New Job', icon: 'jobs', hint: 'Capture work on site' },
  { section: 'quotes', label: 'New Quote', icon: 'quotes', hint: 'Draft a quote' },
  { section: 'invoices', label: 'New Invoice', icon: 'invoices', hint: 'Bill a completed job' },
];

// --- Screens ---------------------------------------------------------------

function homeScreen() {
  const actions = QUICK_ACTIONS.map((a) =>
    h('a', { class: 'quick-action', href: routeToHash({ section: a.section, action: 'new' }) }, [
      icon(ICONS[a.icon]),
      h('span', { class: 'quick-action-label', text: a.label }),
      h('span', { class: 'quick-action-hint', text: a.hint }),
    ]),
  );

  return screen(
    'Sparky',
    [
      h('p', { class: 'screen-lead', text: 'What do you need to do?' }),
      // Hands-free voice mode (Req 11): a headline entry point, one tap from home.
      h('a', { class: 'voice-cta', href: routeToHash({ section: 'voice' }) }, [
        icon(ICONS.mic),
        h('span', { class: 'voice-cta-text' }, [
          h('span', { class: 'voice-cta-label', text: 'Talk to Sparky' }),
          h('span', { class: 'voice-cta-hint', text: 'Hands-free — capture a job or draft a quote by voice' }),
        ]),
      ]),
      h('div', { class: 'quick-actions' }, actions),
      h('nav', { class: 'home-links', 'aria-label': 'More' }, [
        h('a', { class: 'home-link', href: routeToHash({ section: 'clients' }) }, [
          icon(ICONS.clients), h('span', { text: 'Clients' }),
        ]),
        h('a', { class: 'home-link', href: routeToHash({ section: 'settings' }) }, [
          icon(ICONS.settings), h('span', { text: 'Settings & rate card' }),
        ]),
      ]),
    ],
    { section: 'home', hideHeading: true },
  );
}

// A screen scaffold: a scrollable content column with an optional in-body heading.
// `section` lets callers style/identify the screen; `hideHeading` suppresses the
// repeated title on Home (the header already shows the brand).
function screen(title, children, { section, hideHeading = false } = {}) {
  const parts = [];
  if (!hideHeading) parts.push(h('h1', { class: 'screen-title', text: title }));
  parts.push(...[].concat(children));
  return h('section', { class: 'screen', 'data-section': section }, parts);
}

function renderScreen(route) {
  if (route.section === 'home') return homeScreen();
  // Delegate every other section to its real feature screen (task 3.6). The shell
  // still owns the chrome, routing and header title; the screen fills the body and
  // manages its own async loading/error states.
  const featureScreen = renderFeatureScreen(route);
  if (featureScreen) return featureScreen;
  // Defensive fallback: an unknown section (shouldn't happen — the router only
  // yields known sections) degrades to a simple heading rather than a blank body.
  return screen(SECTION_TITLES[route.section] || 'Sparky', [], { section: route.section });
}

// --- Header + bottom nav ---------------------------------------------------

function buildHeader({ onSignOut }) {
  const titleEl = h('span', { class: 'app-brand', text: 'Sparky' });
  const header = h('header', { class: 'app-header' }, [
    titleEl,
    h('div', { class: 'app-header-actions' }, [
      h('a', {
        class: 'icon-btn', href: routeToHash({ section: 'voice' }),
        title: 'Voice mode', 'aria-label': 'Voice mode',
      }, [icon(ICONS.mic)]),
      h('a', {
        class: 'icon-btn', href: routeToHash({ section: 'settings' }),
        title: 'Settings', 'aria-label': 'Settings',
      }, [icon(ICONS.settings)]),
      h('button', {
        class: 'icon-btn', type: 'button', title: 'Sign out', 'aria-label': 'Sign out',
        onclick: onSignOut,
      }, [icon(ICONS.signout)]),
    ]),
  ]);
  return { header, titleEl };
}

function buildBottomNav(onNavigate) {
  const links = NAV_ITEMS.map((item) => {
    const link = h('a', {
      class: 'nav-item', href: routeToHash({ section: item.section }),
      'data-section': item.section,
    }, [
      icon(ICONS[item.section]),
      h('span', { class: 'nav-label', text: item.label }),
    ]);
    // Let the router drive rendering (and highlight) rather than a full reload.
    link.addEventListener('click', (e) => {
      e.preventDefault();
      onNavigate(item.section);
    });
    return link;
  });
  return h('nav', { class: 'bottom-nav', 'aria-label': 'Primary' }, links);
}

function setActiveNav(nav, section) {
  // Home stays highlighted for the header/quick-action flows too.
  for (const link of nav.querySelectorAll('.nav-item')) {
    const active = link.getAttribute('data-section') === section;
    link.classList.toggle('is-active', active);
    if (active) link.setAttribute('aria-current', 'page');
    else link.removeAttribute('aria-current');
  }
}

// --- Mount -----------------------------------------------------------------

// Build the shell into `container` and start routing. `onSignOut` is called when
// the user taps Sign out (app.js clears the Cognito session and shows login).
// Returns a handle exposing the router and a stop() for teardown/tests.
export function mountShell(container, { onSignOut } = {}) {
  clear(container);

  const { header, titleEl } = buildHeader({ onSignOut: () => onSignOut && onSignOut() });
  const main = h('main', { class: 'app-body', id: 'screen', tabindex: '-1' });

  let router; // referenced by the nav click handler below
  const bottomNav = buildBottomNav((section) => router.go({ section }));

  container.appendChild(h('div', { class: 'app-shell' }, [header, main, bottomNav]));

  // The currently mounted screen node. A screen that owns resources beyond its DOM
  // (the voice screen holds a WebSocket + microphone) attaches a `__cleanup`
  // function; we call it before swapping in the next screen and on teardown so a
  // mic is never left capturing after you navigate away (Req 11.7).
  let currentScreen = null;
  const teardownCurrent = () => {
    if (currentScreen && typeof currentScreen.__cleanup === 'function') {
      try {
        currentScreen.__cleanup();
      } catch {
        /* a screen's own teardown must never break navigation */
      }
    }
    currentScreen = null;
  };

  const onRouteChange = (route) => {
    teardownCurrent();
    titleEl.textContent = SECTION_TITLES[route.section] || 'Sparky';
    setActiveNav(bottomNav, route.section);
    currentScreen = renderScreen(route);
    mount(main, currentScreen);
    main.scrollTop = 0;
    main.focus({ preventScroll: true });
  };

  router = createRouter(onRouteChange);
  router.start();

  return {
    router,
    stop: () => {
      teardownCurrent();
      router.stop();
    },
    current: () => router.current(),
  };
}

// --- helpers ---------------------------------------------------------------

// Re-exported so callers/tests can parse without reaching into router.js directly,
// and so task 3.6's screens import the shared feedback UI from the shell.
export { parseRoute };
export { loadingView, errorView, emptyView, runAsync };
